import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { cn } from "./cn";

type NavSection = { number: string; label: string; path: string };

const SITE_NAV: NavSection[] = [
  { number: "01", label: "Pages", path: "pages" },
  { number: "02", label: "Review", path: "review" },
  { number: "03", label: "Collections", path: "collections" },
  { number: "04", label: "Snippets", path: "snippets" },
  { number: "05", label: "Assets", path: "assets" },
  { number: "06", label: "Forms", path: "forms" },
  { number: "07", label: "Theme", path: "theme" },
  { number: "08", label: "Variables", path: "variables" },
  { number: "09", label: "Members", path: "members" },
  { number: "10", label: "Domains", path: "domains" },
  { number: "11", label: "Redirects", path: "redirects" },
  { number: "12", label: "API keys", path: "api-keys" },
  { number: "13", label: "Webhooks", path: "webhooks" },
  { number: "14", label: "Builds", path: "builds" },
  { number: "15", label: "Audit", path: "audit" },
  { number: "16", label: "Settings", path: "settings" },
];

const ACCOUNT_NAV: NavSection[] = [
  { number: "—", label: "Profile", path: "/account" },
  { number: "—", label: "Sessions", path: "/account/sessions" },
  { number: "—", label: "Recovery", path: "/account/recovery-codes" },
];

const COLLAPSE_KEY = "cms.sidebar.collapsed";

export function Shell() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const siteMatch = loc.pathname.match(/^\/sites\/([^/]+)/);
  const siteSlug = siteMatch?.[1] && siteMatch[1] !== "new" ? siteMatch[1] : null;
  const sites = me?.sites ?? [];
  const currentSite = sites.find((s) => s.slug === siteSlug);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  });

  useEffect(() => setSwitcherOpen(false), [loc.pathname]);
  useEffect(() => { if (collapsed) setSwitcherOpen(false); }, [collapsed]);
  useEffect(() => {
    try { window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") { e.preventDefault(); setCollapsed((c) => !c); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!switcherOpen) return;
    function onClick(e: MouseEvent) {
      if (!switcherRef.current?.contains(e.target as Node)) setSwitcherOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [switcherOpen]);

  const siteInitial = (currentSite?.name ?? "?").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen flex bg-[var(--color-paper)] bg-paper-noise">
      <aside
        className={cn(
          "shrink-0 border-r border-[var(--color-rule)] bg-[var(--color-paper-2)] flex flex-col sticky top-0 h-screen transition-[width] duration-200 ease-out",
          collapsed ? "w-[64px]" : "w-[260px]",
        )}
      >
        {/* Brand */}
        <div className={cn("border-b border-[var(--color-rule)] flex items-center", collapsed ? "px-0 py-5 justify-center" : "px-6 pt-7 pb-5 justify-between")}>
          <Link to="/" className="inline-flex items-baseline gap-1.5 text-[var(--color-ink)] no-underline">
            <span className="display text-[26px] leading-none">{collapsed ? "C" : "CMS"}</span>
            <span className="text-[var(--color-accent)] font-mono text-[16px] leading-none">+</span>
          </Link>
          {!collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar (⌘\\)"
              className="text-[var(--color-muted)] hover:text-[var(--color-ink)] transition-colors p-1 -mr-1"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          )}
        </div>
        {!collapsed && <div className="section-no px-6 pt-2 -mt-2 pb-3">Editorial Software</div>}

        {/* Sites switcher */}
        <div className={cn(collapsed ? "px-2 pt-3 pb-1" : "px-4 pt-3 pb-2")} ref={switcherRef}>
          {!collapsed && (
            <div className="label-tracked px-2 mb-2 flex items-center justify-between">
              <span>Workspace</span>
              <Link to="/sites" className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[10px] tracking-[0.12em]">All</Link>
            </div>
          )}
          <div className="relative">
            <button
              onClick={() => collapsed ? nav("/sites") : setSwitcherOpen((o) => !o)}
              title={collapsed ? (currentSite?.name ?? "Sites") : undefined}
              className={cn(
                "w-full text-left flex items-center transition-colors",
                collapsed
                  ? "h-10 justify-center rounded-[var(--radius-sm)] bg-[var(--color-surface)] border border-[var(--color-rule)] hover:border-[var(--color-ink)]"
                  : "justify-between gap-2 px-3 py-2.5 rounded-[var(--radius-sm)] bg-[var(--color-surface)] border border-[var(--color-rule)] hover:border-[var(--color-rule-strong)]",
              )}
            >
              {collapsed ? (
                <span className="display italic text-[15px] text-[var(--color-ink)] leading-none">{siteInitial}</span>
              ) : (
                <>
                  <span className="min-w-0 truncate">
                    <span className="block text-[13.5px] font-medium text-[var(--color-ink)] truncate">
                      {currentSite?.name ?? (sites.length === 0 ? "No site selected" : "Choose a site")}
                    </span>
                    <span className="block text-[11px] font-mono text-[var(--color-muted)] truncate">/{currentSite?.slug ?? "—"}</span>
                  </span>
                  <svg width="10" height="10" viewBox="0 0 10 10" className="shrink-0 text-[var(--color-muted)]">
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
                  </svg>
                </>
              )}
            </button>
            {switcherOpen && !collapsed && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] shadow-[var(--shadow-md)] py-1 max-h-[50vh] overflow-auto">
                {sites.length === 0 && (
                  <div className="px-3 py-2 text-[12.5px] text-[var(--color-muted)]">No sites — create one.</div>
                )}
                {sites.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { nav(`/sites/${s.slug}`); setSwitcherOpen(false); }}
                    className={cn(
                      "w-full text-left px-3 py-2 hover:bg-[var(--color-paper-2)] transition-colors",
                      s.slug === currentSite?.slug && "bg-[var(--color-paper-2)]",
                    )}
                  >
                    <div className="text-[13px] text-[var(--color-ink)] truncate">{s.name}</div>
                    <div className="text-[10.5px] font-mono text-[var(--color-muted)]">/{s.slug} · {s.role}</div>
                  </button>
                ))}
                <div className="border-t border-[var(--color-rule)] mt-1 pt-1">
                  <button
                    onClick={() => { nav("/sites/new"); setSwitcherOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--color-paper-2)] text-[13px] text-[var(--color-accent)] transition-colors"
                  >
                    + New site
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Site nav */}
        <nav className={cn("flex-1 overflow-y-auto pb-4", collapsed ? "px-2 pt-2" : "px-2 pt-3")}>
          {siteSlug && (
            <>
              {!collapsed && <div className="label-tracked px-4 mb-1.5 mt-1">Site</div>}
              <ul>
                {SITE_NAV.map((s) => (
                  <li key={s.path}>
                    <NavLink
                      to={`/sites/${siteSlug}/${s.path}`}
                      title={collapsed ? s.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          "group flex items-baseline no-underline transition-colors",
                          collapsed ? "justify-center py-2 rounded-[3px]" : "gap-3 px-4 py-1.5 text-[13px]",
                          isActive
                            ? "text-[var(--color-ink)] bg-[var(--color-surface)] rounded-[3px]"
                            : "text-[var(--color-ink-2)] hover:text-[var(--color-ink)]",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span className={cn(
                            "font-mono tracking-[0.06em] shrink-0",
                            collapsed ? "text-[11px] w-auto" : "text-[10px] w-5",
                            isActive ? "text-[var(--color-accent)]" : "text-[var(--color-mute-2)] group-hover:text-[var(--color-muted)]",
                          )}>{s.number}</span>
                          {!collapsed && <span>{s.label}</span>}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!siteSlug && !collapsed && (
            <div className="px-4 py-6">
              <p className="text-[12.5px] text-[var(--color-muted)] leading-relaxed">
                Pick a workspace from the switcher above, or browse all sites.
              </p>
              <Link to="/sites" className="mt-3 inline-block text-[13px] text-[var(--color-accent)] hover:text-[var(--color-accent-2)] transition-colors">All sites →</Link>
            </div>
          )}

          {!collapsed && <div className="mt-6 mb-1 label-tracked px-4">Account</div>}
          {collapsed && <div className="my-3 mx-3 border-t border-[var(--color-rule)]" />}
          <ul>
            {ACCOUNT_NAV.map((s) => (
              <li key={s.path}>
                <NavLink
                  to={s.path}
                  end
                  title={collapsed ? s.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      "flex items-baseline no-underline transition-colors",
                      collapsed ? "justify-center py-2 rounded-[3px]" : "gap-3 px-4 py-1.5 text-[13px]",
                      isActive ? "text-[var(--color-ink)] bg-[var(--color-surface)] rounded-[3px]" : "text-[var(--color-ink-2)] hover:text-[var(--color-ink)]",
                    )
                  }
                >
                  <span className={cn("font-mono shrink-0 text-[var(--color-mute-2)]", collapsed ? "text-[12px]" : "text-[10px] w-5")}>{collapsed ? s.label.slice(0, 1) : s.number}</span>
                  {!collapsed && <span>{s.label}</span>}
                </NavLink>
              </li>
            ))}
            {me?.user?.is_superuser && (
              <li>
                <NavLink
                  to="/admin/audit"
                  title={collapsed ? "Admin · Audit" : undefined}
                  className={({ isActive }) => cn(
                    "flex items-baseline no-underline transition-colors",
                    collapsed ? "justify-center py-2 rounded-[3px]" : "gap-3 px-4 py-1.5 text-[13px]",
                    isActive ? "text-[var(--color-ink)] bg-[var(--color-surface)] rounded-[3px]" : "text-[var(--color-ink-2)] hover:text-[var(--color-ink)]",
                  )}
                >
                  <span className={cn("font-mono shrink-0 text-[var(--color-accent)]", collapsed ? "text-[12px]" : "text-[10px] w-5")}>★</span>
                  {!collapsed && <span>Admin · Audit</span>}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* User / footer */}
        <div className={cn("border-t border-[var(--color-rule)]", collapsed ? "px-2 py-3" : "px-4 py-3")}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-9 h-9 rounded-full bg-[var(--color-ink)] text-[var(--color-paper)] flex items-center justify-center font-display italic text-[15px]"
                title={me?.user?.email}
              >
                {(me?.user?.email ?? "?").slice(0, 1).toUpperCase()}
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                title="Expand sidebar (⌘\\)"
                className="text-[var(--color-muted)] hover:text-[var(--color-ink)] transition-colors p-1.5"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[var(--color-ink)] text-[var(--color-paper)] flex items-center justify-center font-display italic text-[15px]">
                  {(me?.user?.email ?? "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-[var(--color-ink)] truncate">{me?.user?.email}</div>
                  {me?.user?.is_superuser && <div className="text-[10px] text-[var(--color-accent)] font-mono uppercase tracking-[0.1em]">Superuser</div>}
                </div>
              </div>
              <button
                onClick={async () => { await logout(); nav("/login"); }}
                className="mt-3 w-full text-left px-2 py-1.5 text-[12px] text-[var(--color-muted)] hover:text-[var(--color-rust)] transition-colors"
              >
                Sign out ↗
              </button>
            </>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
